import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, ArrowRight, Zap, Shield, Droplet } from "lucide-react";
import SectionHeader from "../components/SectionHeader";

const PRODUCT_IMG = "https://images.pexels.com/photos/28824108/pexels-photo-28824108.jpeg";

export default function ProductsPage() {
  const { t } = useTranslation();
  const products = [
    { key: "instaclean_100", icon: Zap, color: "amber", testid: "product-instaclean-100", tag: "FLAGSHIP" },
    { key: "instaclean_200", icon: Shield, color: "blue", testid: "product-instaclean-200", tag: "NON-FLAMMABLE" },
    { key: "instaclean_50", icon: Droplet, color: "slate", testid: "product-instaclean-50", tag: "HEAVY-DUTY" },
  ];

  return (
    <div data-testid="products-page" className="pb-24">
      <section className="relative border-b border-slate-800 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-25" style={{ backgroundImage: `url(${PRODUCT_IMG})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 to-slate-950" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-24">
          <SectionHeader kicker="PRODUCT LINE" title={t("products.title")} description={t("products.subtitle")} testIdPrefix="products" />
        </div>
      </section>

      <section className="py-20 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 space-y-10">
          {products.map((p, idx) => {
            const name = t(`products.${p.key}.name`);
            const tagline = t(`products.${p.key}.tagline`);
            const bullets = t(`products.${p.key}.bullets`, { returnObjects: true }) || [];
            return (
              <article
                key={p.key}
                data-testid={p.testid}
                className="grid md:grid-cols-12 gap-0 border border-slate-800 bg-slate-950 hover:border-amber-500/40 transition-colors"
              >
                <div className="md:col-span-4 p-10 border-r border-slate-800 flex flex-col justify-between">
                  <div>
                    <div className="font-mono-spec text-[10px] text-amber-400">{p.tag}</div>
                    <p.icon className="mt-4 text-amber-400" size={28} />
                    <h3 className="mt-6 font-display text-3xl font-extrabold text-white">{name}</h3>
                    <p className="mt-2 text-blue-300 font-mono-spec text-xs">{tagline}</p>
                  </div>
                  <div className="mt-10 pt-6 border-t border-slate-800 font-mono-spec text-xs text-slate-500">
                    PRODUCT / 0{idx + 1}
                  </div>
                </div>
                <div className="md:col-span-8 p-10">
                  <ul className="space-y-4">
                    {bullets.map((b, i) => (
                      <li key={i} className="flex items-start gap-3" data-testid={`${p.testid}-bullet-${i}`}>
                        <Check size={18} className="text-amber-400 flex-shrink-0 mt-1" />
                        <span className="text-slate-300 leading-relaxed">{b}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8 flex gap-4">
                    <Link
                      to="/demo"
                      data-testid={`${p.testid}-demo-cta`}
                      className="inline-flex items-center gap-2 bg-amber-500 text-slate-950 font-bold px-5 py-2.5 hover:bg-amber-400 transition-colors rounded-sm text-sm"
                    >
                      Book a Demo <ArrowRight size={14} />
                    </Link>
                    <Link
                      to="/contact"
                      data-testid={`${p.testid}-contact-cta`}
                      className="inline-flex items-center gap-2 border border-slate-700 text-slate-200 px-5 py-2.5 hover:border-white rounded-sm text-sm"
                    >
                      Technical spec
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* FAPS callout */}
      <section className="py-20" data-testid="faps-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid md:grid-cols-2 gap-0 border border-slate-800">
            <div className="p-10 lg:p-14 bg-slate-950">
              <div className="font-mono-spec text-xs text-amber-400">FAPS</div>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold text-white leading-tight">
                Fire & Arc Protection Solutions.
              </h2>
              <p className="mt-5 text-slate-400 leading-relaxed">
                An engineered portfolio of intumescent paint schemes, passive fire barriers, and active non-flammable suppressants — built for the chain of failure we diagnose in every panel we open.
              </p>
              <ul className="mt-6 space-y-3 text-sm text-slate-300">
                <li className="flex items-start gap-2"><Check size={16} className="text-amber-400 mt-0.5" /> Engineered intumescent paint schemes for structural steel</li>
                <li className="flex items-start gap-2"><Check size={16} className="text-amber-400 mt-0.5" /> Non-flammable arc-suppression for critical switchgear</li>
                <li className="flex items-start gap-2"><Check size={16} className="text-amber-400 mt-0.5" /> Passive fire-stop barriers for cable penetrations</li>
                <li className="flex items-start gap-2"><Check size={16} className="text-amber-400 mt-0.5" /> Insurance-aligned documentation & certifications</li>
              </ul>
              <Link
                to="/contact"
                data-testid="faps-cta"
                className="mt-8 inline-flex items-center gap-2 border border-amber-500 text-amber-400 px-5 py-2.5 hover:bg-amber-950/40 rounded-sm text-sm font-semibold"
              >
                Request FAPS dossier <ArrowRight size={14} />
              </Link>
            </div>
            <div className="bg-slate-900 p-10 lg:p-14 grid grid-cols-2 gap-0 border-l border-slate-800 grid-border">
              {[
                { t: "Detection", d: "Thermal + arc-fault sensors" },
                { t: "Prevention", d: "Intumescent & ceramic coatings" },
                { t: "Suppression", d: "Non-flammable live-load agents" },
                { t: "Training", d: "AI-generated SOP videos" },
              ].map((x, i) => (
                <div key={i} className="p-8 bg-slate-950" data-testid={`faps-tile-${i}`}>
                  <div className="font-mono-spec text-[10px] text-amber-400">LAYER {i + 1}</div>
                  <div className="mt-3 font-display text-lg font-bold text-white">{x.t}</div>
                  <div className="mt-1 text-sm text-slate-400">{x.d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
